import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { X } from "lucide-react";
import { DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { getAppointmentsByDateRange } from "@/lib/api";
import { sessionsOn, useClinicHours } from "@/hooks/useClinicHours";
import { cn } from "@/lib/utils";

// Shared by New appointment and Edit appointment: the dialog frame, labels, the day and the time slots.

const ymd = (d: Date) => format(d, "yyyy-MM-dd");
const toDate = (day: string) => new Date(`${day}T00:00:00`);

/** "Thu, 08 Oct" */
export const dayLabel = (day: string) => format(toDate(day), "EEE, dd MMM");
/** "5:30 PM" from "17:30" or "17:30:00" */
export const timeLabel = (time: string) => format(new Date(`1970-01-01T${time.slice(0, 5)}:00`), "h:mm a");

/**
 * The dialog frame: 480px box on tablets and desktops, a sheet from the bottom on phones; a 16px title with a
 * 36px close button; the body scrolls and the footer stays in view.
 */
export function AppointmentDialogContent({ title, onClose, children, footer, onPointerDownOutside }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  onPointerDownOutside?: React.ComponentProps<typeof DialogContent>["onPointerDownOutside"];
}) {
  return (
    <DialogContent
      showClose={false}
      aria-describedby={undefined}
      onPointerDownOutside={onPointerDownOutside}
      className={cn(
        "max-w-[480px] p-0 gap-0 flex flex-col max-h-[90dvh] overflow-hidden",
        "kb-sheet max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:max-w-none max-sm:rounded-t-xl max-sm:border-x-0 max-sm:border-b-0",
      )}
    >
      <div className="flex items-center justify-between gap-3 pl-5 pr-3 h-14 border-b border-border shrink-0">
        <DialogTitle className="text-base font-semibold text-foreground">{title}</DialogTitle>
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="inline-flex h-9 w-9 touch:h-11 touch:w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">{children}</div>
      <div data-kb-footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {footer}
      </div>
    </DialogContent>
  );
}

/** A field's 13px label, with what's wrong underneath once the form was tried */
export function Field({ label, htmlFor, error, children }: { label: string; htmlFor?: string; error?: string | null; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-label mb-1.5">{label}</label>
      {children}
      {error && <p role="alert" className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Today / Tomorrow / a picked date, and the day written out */
export function DayPicker({ value, onChange, disabled }: { value: string; onChange: (day: string) => void; disabled?: boolean }) {
  const today = ymd(new Date());
  const tomorrow = ymd(addDays(new Date(), 1));
  const which = value === today ? "today" : value === tomorrow ? "tomorrow" : "pick";
  // Looks like Segmented; "Pick date" is the browser's own date field laid over the option, so a tap opens its
  // calendar on every device (opening it from script doesn't work on older iPads)
  const option = (active: boolean) => cn(
    "relative inline-flex h-full items-center justify-center whitespace-nowrap rounded-md px-3 text-[13px] transition-colors",
    "focus-within:ring-2 focus-within:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active ? "bg-background text-foreground font-medium shadow-sm" : "text-label hover:text-foreground",
  );

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div role="group" aria-label="Day" className="inline-flex h-8 touch:h-10 items-center gap-0.5 rounded-lg bg-muted p-0.5">
        <button type="button" aria-pressed={which === "today"} disabled={disabled} onClick={() => onChange(today)} className={option(which === "today")}>Today</button>
        <button type="button" aria-pressed={which === "tomorrow"} disabled={disabled} onClick={() => onChange(tomorrow)} className={option(which === "tomorrow")}>Tomorrow</button>
        <label className={cn(option(which === "pick"), "cursor-pointer")}>
          {which === "pick" ? dayLabel(value) : "Pick date"}
          <input
            type="date" value={value} min={today} disabled={disabled} aria-label="Pick a date"
            onChange={e => e.target.value && onChange(e.target.value)}
            // Desktop Chrome opens the calendar only from its icon; a click anywhere should open it
            onClick={e => { try { e.currentTarget.showPicker(); } catch { /* the browser opens it itself */ } }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </label>
      </div>
      {which !== "pick" && <span className="text-sm text-muted-foreground">{dayLabel(value)}</span>}
    </div>
  );
}

type Part = "morning" | "afternoon" | "evening";
const PARTS: { value: Part; label: string; from: number; to: number }[] = [
  { value: "morning", label: "Morning", from: 8, to: 12 },
  { value: "afternoon", label: "Afternoon", from: 12, to: 17 },
  { value: "evening", label: "Evening", from: 17, to: 22 },
];
const partOf = (time: string): Part => {
  const h = Number(time.slice(0, 2));
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
};
const pad = (n: number) => String(n).padStart(2, "0");

/**
 * 15-minute slots while the clinic is open (Settings → Clinic hours; 8 AM to 10 PM when the hours aren't set), a part of
 * the day at a time. Past slots are left out today. A slot shows how
 * many of this doctor's patients are already booked in it, but stays bookable: clinics double-book.
 * "Other time" takes any exact time.
 */
export function TimeSlots({ day, value, onChange, doctorId, clinicId, ignoreAppointmentId, disabled }: {
  day: string;
  value: string;
  onChange: (time: string) => void;
  doctorId?: string;
  clinicId?: string;
  /** Edit: the appointment being moved doesn't count against its own slot */
  ignoreAppointmentId?: string;
  disabled?: boolean;
}) {
  const isToday = day === ymd(new Date());
  const nowHm = format(new Date(), "HH:mm");
  const [booked, setBooked] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (!doctorId || !clinicId) { setBooked(new Map()); return; }
    let cancelled = false;
    getAppointmentsByDateRange(day, day, clinicId, doctorId)
      .then(list => {
        if (cancelled) return;
        const counts = new Map<string, number>();
        for (const a of list || []) {
          if (!a.time || a.id === ignoreAppointmentId || a.status === "cancelled") continue;
          // A booking at 5:40 counts in the 5:30 slot
          const [h, m] = a.time.slice(0, 5).split(":").map(Number);
          const slot = `${pad(h)}:${pad(Math.floor(m / 15) * 15)}`;
          counts.set(slot, (counts.get(slot) ?? 0) + 1);
        }
        setBooked(counts);
      })
      .catch(() => { if (!cancelled) setBooked(new Map()); });
    return () => { cancelled = true; };
  }, [day, doctorId, clinicId, ignoreAppointmentId]);

  // undefined: the clinic hasn't set its hours; [] closed that day
  const sessions = sessionsOn(useClinicHours(), day);
  const slotsOf = (p: (typeof PARTS)[number]) => {
    const out: string[] = [];
    // With hours set, a session may start before 8 or run to midnight
    const from = sessions && p.value === "morning" ? 0 : p.from;
    const to = sessions && p.value === "evening" ? 24 : p.to;
    for (let h = from; h < to; h++) for (let m = 0; m < 60; m += 15) out.push(`${pad(h)}:${pad(m)}`);
    return out.filter(t => (!sessions || sessions.some(x => t >= x.start && t < x.end)) && (!isToday || t > nowHm));
  };
  const partsWithSlots = PARTS.filter(p => slotsOf(p).length > 0);

  // Open on the chosen time's part of the day; otherwise the current one (today) or the morning
  const [part, setPart] = useState<Part>(() => partOf(value || (isToday ? nowHm : "08:00")));
  useEffect(() => {
    if (!partsWithSlots.some(p => p.value === part) && partsWithSlots[0]) setPart(partsWithSlots[0].value);
  }, [day, sessions?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSlots = useMemo(() => PARTS.flatMap(slotsOf), [day, nowHm, sessions]); // eslint-disable-line react-hooks/exhaustive-deps
  const [other, setOther] = useState(false);
  const showOther = other || (!!value && !allSlots.includes(value.slice(0, 5)));
  const current = PARTS.find(p => p.value === part)!;

  if (partsWithSlots.length === 0 && !showOther) {
    return (
      <p className="text-sm text-muted-foreground">
        {sessions?.length === 0
          ? `The clinic is closed on ${format(new Date(`${day}T00:00:00`), "EEEE")}s.`
          : isToday ? "No slots left today." : "No slots this day."}{" "}
        <button type="button" onClick={() => setOther(true)} className="font-medium text-primary hover:underline underline-offset-2">Other time</button>
      </p>
    );
  }

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!showOther && (
          <Segmented
            aria-label="Part of the day"
            value={part}
            onChange={v => setPart(v as Part)}
            options={partsWithSlots.map(p => ({ value: p.value, label: p.label }))}
          />
        )}
        <button
          type="button" onClick={() => setOther(o => !o)} disabled={disabled}
          className="ml-auto text-[13px] font-medium text-primary hover:underline underline-offset-2"
        >
          {showOther ? "Show slots" : "Other time"}
        </button>
      </div>

      {showOther ? (
        <Input
          type="time" value={value.slice(0, 5)} onChange={e => onChange(e.target.value)}
          min={isToday ? nowHm : undefined} disabled={disabled} aria-label="Time" className="h-9 text-sm w-40"
        />
      ) : (
        <div role="radiogroup" aria-label={`${current.label} slots`} className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
          {slotsOf(current).map(t => {
            const n = booked.get(t) ?? 0;
            const selected = value.slice(0, 5) === t;
            return (
              <button
                key={t} type="button" role="radio" aria-checked={selected} disabled={disabled}
                onClick={() => onChange(t)}
                aria-label={`${timeLabel(t)}${n ? `, ${n} booked` : ""}`}
                className={cn(
                  "h-9 touch:h-11 rounded-md border text-[13px] font-mono tabular-nums transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : n > 0
                      ? "border-field bg-muted text-foreground hover:border-primary/60"
                      : "border-field bg-background text-foreground hover:border-primary/60",
                )}
              >
                {format(new Date(`1970-01-01T${t}:00`), "h:mm")}
                {n > 0 && <span className={cn("ml-1 text-2xs font-sans", selected ? "text-primary-foreground/80" : "text-muted-foreground")}>·{n}</span>}
              </button>
            );
          })}
        </div>
      )}
      {booked.size > 0 && !showOther && (
        <p className="text-xs text-muted-foreground">Grey slots already have patients booked (·n). They can still be booked.</p>
      )}
    </div>
  );
}
